// ============================================
// WOMEN'S SEXUAL HEALTH FOLLOW-UP QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health "Women's Sexual Health Follow Up Questionnaire" (2025).
// Visit Type: womenSexualHealthFollowup
// No labs. Mirrors the trtFollowUpData structure (questions map + flow array +
// showIf conditionals + textarea-prefilled named fields + consent disqualifiers).
//
// Color legend: BLACK = Q/A, BLUE = conditional, PURPLE = named-field (excluded
// from Q/A by the backend), RED = disqualifying, PINK = product routing (none
// here — follow-ups continue the parent visit's patientPreference).
//
// See docs/BACKEND_WOMEN_SEXUAL_HEALTH_FOLLOWUP_PRD.md for the canonical contract.

const TRUTHFULNESS_CONSENT_BODY = `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions and that my responses are true, accurate, and complete to the best of my knowledge. I understand that providing inaccurate or incomplete information may affect the safety and appropriateness of my treatment.`;

const MAIN_CONSENT_BODY = `Consent — PT-141, Oxytocin, and Medications for Female Libido

This consent form provides detailed information about PT-141 (Bremelanotide), oxytocin, and other medications prescribed to address low female libido. It is essential that you read and understand this information before proceeding.

Purpose and Benefits:
Medications such as PT-141 and oxytocin are used to enhance sexual desire through complementary mechanisms:
- PT-141 (Bremelanotide): acts centrally by stimulating melanocortin receptors, which may boost sexual desire and can lead to increased arousal and improved overall sexual satisfaction.
- Oxytocin: often known as the "bonding hormone," oxytocin is believed to promote emotional closeness and trust between partners and may enhance intimacy and contribute to a more fulfilling sexual experience.

These treatments aim to improve libido, foster intimacy, and contribute to overall emotional well-being.

Potential Risks and Side Effects:
While these medications offer potential benefits, they also carry risks and side effects that may vary from person to person.

Common side effects — PT-141:
- Nausea or upset stomach
- Headaches
- Flushing or redness
- Dizziness or lightheadedness
- Injection site reactions (if applicable)
- Skin hyperpigmentation (1-38% risk)

Common side effects — Oxytocin:
- Mild headache
- Nausea
- Temporary changes in blood pressure
- Possible mood alterations

Rare but Serious Risks:
- Significant changes in blood pressure
- Severe allergic reactions, including swelling of the face, lips, tongue, or throat
- Noticeable changes in mood or behavior that cause concern

Allergy warning: this formulation may contain inactive ingredients that could trigger allergic reactions. If you experience severe symptoms such as chest pain, difficulty breathing, extreme dizziness, or signs of an allergic reaction, seek immediate medical attention.

Patients with poorly controlled blood pressure or known cardiovascular disease/heart disease should not take these products.

Monitoring Plan:
Your healthcare provider will monitor your progress and response to these treatments to ensure their continued safety and effectiveness. This includes attending all scheduled follow-up appointments, routine evaluations and necessary diagnostic tests, and reporting any new or worsening symptoms promptly.

Alternative Treatments:
You have the right to discuss alternative approaches for managing low female libido, which may include non-pharmacological therapies (counseling, sex therapy, or relationship therapy), lifestyle modifications, or other medications or hormonal therapies.

Your Responsibilities:
As a patient undergoing this treatment, you agree to follow the prescribed dosage and administration instructions precisely, attend all follow-up appointments, inform your healthcare provider of any new medications or changes in your health status, and report any concerning side effects or unusual symptoms immediately.

By selecting below, you confirm that you have read and understood the information provided. You acknowledge the potential benefits and risks associated with treatment and consent to proceed.`;

// ---------- PURPLE textarea slides (extracted to named fields, prefilled) ----------

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea-prefilled',
  question: 'Please identify all your current medical conditions',
  fieldMapping: 'medicalConditions',
  placeholder: 'e.g., Hypertension, Asthma, etc.',
  required: true,
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea-prefilled',
  question: 'Please list all your current medications including dosages',
  fieldMapping: 'selfReportedMeds',
  placeholder: 'e.g., Lisinopril 10mg daily, Vitamin D 2000 IU daily…',
  required: true,
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea-prefilled',
  question: 'Please list all of your known allergies',
  fieldMapping: 'allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
  required: true,
};

// ---------- Health / medication changes ----------

const healthChangesQuestion = {
  id: 'health-changes',
  type: 'single-select',
  question:
    'Have there been any health or medication changes, ED visits or hospitalizations since your last check-in?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const healthChangesDetailsQuestion = {
  id: 'health-changes-details',
  type: 'textarea',
  question: 'Please tell us more about the changes in your health',
  showIf: { field: 'health-changes', value: 'yes' },
  placeholder: 'Describe your health changes…',
  required: true,
};

const medicationResponseQuestion = {
  id: 'medication-response',
  type: 'single-select',
  question: 'How are you doing on the current medication?',
  options: [
    { label: 'Well', value: 'well' },
    { label: 'Not well', value: 'not-well' },
  ],
};

const medicationResponseDetailsQuestion = {
  id: 'medication-response-details',
  type: 'textarea',
  question: 'Please tell us more about what you are experiencing',
  showIf: { field: 'medication-response', value: 'not-well' },
  placeholder: 'Describe what you are experiencing…',
  required: true,
};

const sideEffectsQuestion = {
  id: 'side-effects',
  type: 'single-select',
  question: 'Have you experienced any side effects from the current medication?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const sideEffectsDetailsQuestion = {
  id: 'side-effects-details',
  type: 'textarea',
  question: 'Please tell us more about the side effects you are experiencing',
  showIf: { field: 'side-effects', value: 'yes' },
  placeholder: 'Describe the side effects…',
  required: true,
};

// ---------- Blood pressure branch ----------

const highBpQuestion = {
  id: 'high-bp',
  type: 'single-select',
  question: 'Do you have high blood pressure?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const bpControlledQuestion = {
  id: 'bp-controlled',
  type: 'single-select',
  question: 'Is your blood pressure typically well controlled?',
  showIf: { field: 'high-bp', value: 'yes' },
  options: [
    { label: 'Yes', value: 'yes' },
    {
      label: 'No',
      value: 'no',
      disqualifies: true,
      disqualifyReason:
        'Because your blood pressure is not well controlled, these medications are not safe for you right now. Please follow up with your primary care provider.',
    },
  ],
};

const bpRecentReadingQuestion = {
  id: 'bp-recent-reading',
  type: 'textarea',
  question:
    'Please provide a recent blood pressure reading taken within the last 4-6 weeks',
  showIf: { field: 'bp-controlled', value: 'yes' },
  placeholder: 'e.g., 118/76',
  required: true,
};

// ---------- Treatment preference ----------

const treatmentPreferenceQuestion = {
  id: 'treatment-preference',
  type: 'single-select',
  question:
    'Would you like to continue your current treatment, change the dose, or change the medication?',
  options: [
    { label: "I'm doing well and I would like to continue what I'm on", value: 'continue' },
    { label: 'I would like to change the medication if there are other options', value: 'change-medication' },
    { label: "I'm not sure, have the provider reach out to discuss further", value: 'unsure' },
  ],
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Type your questions or concerns here…',
  required: false,
};

const truthfulnessConsentQuestion = {
  id: 'truthfulness-consent',
  type: 'consent-dropdown',
  question: 'Truthfulness Attestation',
  consentText: TRUTHFULNESS_CONSENT_BODY,
  options: [
    { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true },
  ],
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'PT-141, Oxytocin, and Medications for Female Libido — Informed Consent',
  consentText: MAIN_CONSENT_BODY,
  options: [
    { label: 'I have read the above information, I understand the risks, and I would like to proceed.', value: 'consent' },
    { label: 'I have read the above information, and I do NOT wish to proceed.', value: 'decline', disqualifies: true },
  ],
};

const checkoutQuestion = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay',
};

// ---------- Question registry ----------

export const womenSexualHealthFollowUpQuestions = {
  medicalConditions: medicalConditionsQuestion,
  selfReportedMeds: selfReportedMedsQuestion,
  allergies: allergiesQuestion,
  healthChanges: healthChangesQuestion,
  healthChangesDetails: healthChangesDetailsQuestion,
  medicationResponse: medicationResponseQuestion,
  medicationResponseDetails: medicationResponseDetailsQuestion,
  sideEffects: sideEffectsQuestion,
  sideEffectsDetails: sideEffectsDetailsQuestion,
  highBp: highBpQuestion,
  bpControlled: bpControlledQuestion,
  bpRecentReading: bpRecentReadingQuestion,
  treatmentPreference: treatmentPreferenceQuestion,
  additionalQuestions: additionalQuestionsQuestion,
  truthfulnessConsent: truthfulnessConsentQuestion,
  mainConsent: mainConsentQuestion,
  checkout: checkoutQuestion,
};

// Flow order matches the Beluga source-of-truth docx top-to-bottom.
export const womenSexualHealthFollowUpFlow = [
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'health-changes',
  'health-changes-details',
  'medication-response',
  'medication-response-details',
  'side-effects',
  'side-effects-details',
  'high-bp',
  'bp-controlled',
  'bp-recent-reading',
  'treatment-preference',
  'additional-questions',
  'truthfulness-consent',
  'main-consent',
  'checkout',
];

export const getWomenSexualHealthFollowUpQuestionById = (id) =>
  Object.values(womenSexualHealthFollowUpQuestions).find((q) => q.id === id);

// Builds the visible flow given current answers. BLUE conditionals:
//   health-changes-details        ← health-changes='yes'
//   medication-response-details   ← medication-response='not-well'
//   side-effects-details          ← side-effects='yes'
//   bp-controlled                 ← high-bp='yes'   (No → disqualifies)
//   bp-recent-reading             ← bp-controlled='yes'
export const buildDynamicWomenSexualHealthFollowUpFlow = (answers /* , userData */) => {
  const flow = [];
  for (const questionId of womenSexualHealthFollowUpFlow) {
    const question = getWomenSexualHealthFollowUpQuestionById(questionId);
    if (!question) continue;

    if (question.showIf) {
      const { field, value, includes } = question.showIf;
      const current = answers[field];
      if (includes !== undefined) {
        if (!Array.isArray(current) || !current.includes(includes)) continue;
      } else if (current !== value) {
        continue;
      }
    }

    flow.push(question);
  }
  return flow;
};
